import { Link } from '@tanstack/react-router';
import type {
  ColumnDef,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';

import type {
  EquityRow,
  ETFRow,
  MutualFundRow,
} from '#/api/generated/fINRAMarketDataAPI.schemas';
import { DataTable } from '#/components/data/DataTable';
import { Icon } from '#/components/ui/icons';
import { cn } from '#/utils/cn';

type Row = EquityRow | ETFRow | MutualFundRow;

export function SearchResultsSection<T extends Row>({
  data,
  query,
  investmentType,
  title,
  showTitle,
  getRowId,
  columnDefs,
  emptyMessage,
  displayRowCount,
  sorting,
  onSortingChange,
  manualSorting = false,
  trackContext,
}: {
  data: T[];
  query: string;
  investmentType: string;
  title: string;
  showTitle: boolean;
  getRowId: (row: T, index: number) => string;
  columnDefs: ColumnDef<T>[];
  emptyMessage?: string | null;
  displayRowCount: number;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;
  /** Analytics grouping bucket forwarded to the table's sort headers. */
  trackContext?: string;
}) {
  if (!data.length && emptyMessage === null) {
    return null;
  }

  return (
    <section className="flex flex-col gap-12">
      <h2
        className={cn(
          'text-fluid-base text-fg-header font-semibold',
          showTitle ? '' : 'sr-only',
        )}
      >
        {title}
      </h2>

      <DataTable
        aria-label={`${title} results`}
        columns={columnDefs}
        enableSortingRemoval
        getRowId={getRowId}
        emptyState={emptyMessage ?? 'No results'}
        data={data.slice(0, displayRowCount)}
        sorting={sorting}
        onSortingChange={onSortingChange}
        manualSorting={manualSorting}
        trackContext={trackContext}
        className="-mx-gap sm:mx-0"
      />

      <div>
        {data.length > displayRowCount && (
          <Link
            to="/search"
            search={{
              query,
              order_by: undefined,
              sort_by: undefined,
              investment_type: investmentType,
              equity_sector_slug: undefined,
              equity_industry_slug: undefined,
              market_cap_categories: undefined,
              fund_category_slug: undefined,
              equity_next_pagination_token: undefined,
              equity_prev_pagination_token: undefined,
              etf_next_pagination_token: undefined,
              etf_prev_pagination_token: undefined,
              mutual_fund_next_pagination_token: undefined,
              mutual_fund_prev_pagination_token: undefined,
              listing_type: undefined,
              exchange: undefined,
            }}
            className="mdc-btn mdc-btn--md mdc-btn--link group"
            data-track="search_view_filter_type"
            data-track-context="search_results"
          >
            View & Filter {title}{' '}
            <Icon
              id="arrow-right"
              size={14}
              aria-hidden="true"
              className="transition-transform group-hover:translate-x-4"
            />
          </Link>
        )}
      </div>
    </section>
  );
}
